import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import type { CannedReply } from '@repletoryreach/sdk-api/inbox';
import { Badge, Kbd, Text } from '@repletoryreach/sdk-ui';
import { whatsAppPreview } from '@repletoryreach/sdk-utils/whatsapp-text';
import { useT } from '../../../i18n/I18nProvider';

/** Query key prefix shared with the settings page so edits invalidate the picker too. */
export const CANNED_REPLIES_KEY = ['canned-replies'] as const;

/**
 * W37: matches for the "/" autocomplete. The org's replies are fetched once
 * (they are few) and filtered client-side by shortcut prefix or title
 * substring; the server-side `search` is exercised by the settings page.
 */
export function useCannedReplyMatches(query: string, enabled: boolean, max = 8): { matches: CannedReply[]; isLoading: boolean } {
  const all = useQuery({
    queryKey: [...CANNED_REPLIES_KEY, 'picker'],
    queryFn: () => inbox.listCannedReplies({ limit: 200 }),
    enabled,
    staleTime: 60_000
  });
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    const rows = all.data?.data ?? [];
    const hit = term
      ? rows.filter((r) => r.shortcut.startsWith(term) || r.title.toLowerCase().includes(term))
      : rows;
    return hit.slice(0, max);
  }, [all.data, query, max]);
  return { matches, isLoading: all.isLoading };
}

export interface CannedReplyPickerProps {
  listId: string;
  matches: CannedReply[];
  loading: boolean;
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (reply: CannedReply) => void;
}

/** Popover listbox rendered above the composer; keyboard handling lives in the composer. */
export function CannedReplyPicker({ listId, matches, loading, activeIndex, onHover, onSelect }: CannedReplyPickerProps) {
  const t = useT();
  return (
    <div className="rr-inbox2__picker">
      {matches.length === 0 ? (
        <div className="rr-inbox2__picker-empty">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {loading ? t('common.loading') : t('inbox.canned.noMatch')}
          </Text>
          <Link to="/settings/canned-replies">{t('inbox.canned.manage')}</Link>
        </div>
      ) : (
        <ul id={listId} role="listbox" aria-label={t('canned.title')} className="rr-inbox2__picker-list">
          {matches.map((r, i) => (
            <li
              key={r.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`rr-inbox2__picker-item${i === activeIndex ? ' is-active' : ''}`}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => { e.preventDefault(); onSelect(r); }}
            >
              <div className="rr-inbox2__picker-head">
                <strong>/{r.shortcut}</strong>
                <span>{r.title}</span>
                <Badge size="xs" variant="light">{r.language}</Badge>
              </div>
              <div className="rr-inbox2__picker-preview">{whatsAppPreview(r.body, 120)}</div>
            </li>
          ))}
        </ul>
      )}
      <div className="rr-inbox2__picker-hint">
        <Kbd>↑</Kbd><Kbd>↓</Kbd> {t('inbox.canned.navigate')} · <Kbd>{t('inbox.cannedReplies.keyEnter')}</Kbd> {t('inbox.canned.insert')} · <Kbd>{t('inbox.cannedReplies.keyEsc')}</Kbd> {t('inbox.canned.close')}
      </div>
    </div>
  );
}
